/**
 * Shape GeoJSON Helpers
 *
 * Utility functions for generating shape geometries using Turf.js.
 * Converts Shape geometry points to GeoJSON Polygon format for MapLibre.
 * Part of multi-geometry support (ETAP 4).
 *
 * @module components/map/GeometryRenderer/ShapeRenderer/utils/shapeGeoJsonHelpers
 */

import * as turf from '@turf/turf';
import { ShapeType } from '../../../../../types/geometry';
import type { TaskGeometry, Coordinates } from '../../../../../types';
import type { Feature, Polygon } from 'geojson';

/**
 * Create GeoJSON Polygon feature from Shape geometry
 *
 * Generates appropriate polygon based on shape type:
 * - Rectangle: 4 corner points (TL, TR, BR, BL)
 * - Circle: 4 cardinal points (N, S, E, W) → turf.circle()
 * - Triangle: 3 vertex points
 *
 * @param params - Object containing geometry
 * @param params.geometry - Shape geometry with shapeType and shapePoints
 * @returns GeoJSON Feature with Polygon geometry
 *
 * @example
 * ```typescript
 * const geometry = {
 *   id: 'geom-123',
 *   type: GeometryType.Shape,
 *   shapeType: ShapeType.Circle,
 *   shapePoints: [
 *     { lat: 52.01, lng: 21.0 }, // North
 *     { lat: 51.99, lng: 21.0 }, // South
 *     { lat: 52.0, lng: 21.01 }, // East
 *     { lat: 52.0, lng: 20.99 }, // West
 *   ],
 * };
 * const geoJson = createShapeGeoJson({ geometry });
 * // Returns GeoJSON Polygon with circle geometry
 * ```
 */
export function createShapeGeoJson({
  geometry,
}: {
  geometry: TaskGeometry;
}): Feature<Polygon> {
  const { shapeType, shapePoints } = geometry;

  if (!shapePoints || shapePoints.length === 0) {
    throw new Error(`Geometry ${geometry.id} has no shape points`);
  }

  let polygon: Feature<Polygon>;

  switch (shapeType) {
    case ShapeType.Rectangle: {
      // Rectangle: 4 corner points (TL, TR, BR, BL)
      if (shapePoints.length < 4) {
        throw new Error(
          `Geometry ${geometry.id} Rectangle requires 4 points, got ${shapePoints.length}`
        );
      }

      // Convert to [lng, lat] format and close polygon
      // Type assertion is safe after validation check above
      const [p0, p1, p2, p3] = shapePoints as [
        Coordinates,
        Coordinates,
        Coordinates,
        Coordinates,
      ];
      const coordinates = [
        [
          [p0.lng, p0.lat], // TL
          [p1.lng, p1.lat], // TR
          [p2.lng, p2.lat], // BR
          [p3.lng, p3.lat], // BL
          [p0.lng, p0.lat], // Close polygon
        ],
      ];

      polygon = turf.polygon(coordinates);
      break;
    }

    case ShapeType.Circle: {
      // Circle: 4 cardinal points (North, South, East, West)
      if (shapePoints.length < 4) {
        throw new Error(
          `Geometry ${geometry.id} Circle requires 4 cardinal points, got ${shapePoints.length}`
        );
      }

      // Type assertion is safe after validation check above
      const [north, south, east, west] = shapePoints as [
        Coordinates,
        Coordinates,
        Coordinates,
        Coordinates,
      ];

      // Calculate center from 4 cardinal points (average of opposite points)
      const centerLat = (north.lat + south.lat) / 2;
      const centerLng = (east.lng + west.lng) / 2;
      const center = [centerLng, centerLat] as [number, number];

      // Calculate radius as distance from center to any cardinal point (using North)
      const radiusPoint = [north.lng, north.lat] as [number, number];
      const radius = turf.distance(center, radiusPoint, { units: 'meters' });

      // Generate circle with 64 steps for smooth rendering
      polygon = turf.circle(center, radius, {
        steps: 64,
        units: 'meters',
      });
      break;
    }

    case ShapeType.Triangle: {
      // Triangle: 3 vertex points
      if (shapePoints.length < 3) {
        throw new Error(
          `Geometry ${geometry.id} Triangle requires 3 points, got ${shapePoints.length}`
        );
      }

      // Convert to [lng, lat] format and close polygon
      // Type assertion is safe after validation check above
      const [v0, v1, v2] = shapePoints as [
        Coordinates,
        Coordinates,
        Coordinates,
      ];
      const coordinates = [
        [
          [v0.lng, v0.lat],
          [v1.lng, v1.lat],
          [v2.lng, v2.lat],
          [v0.lng, v0.lat], // Close polygon
        ],
      ];

      polygon = turf.polygon(coordinates);
      break;
    }

    case ShapeType.Polygon: {
      // Generic polygon: N vertices (N ≥ 3) forming a closed ring.
      if (shapePoints.length < 3) {
        throw new Error(
          `Geometry ${geometry.id} Polygon requires at least 3 points, got ${shapePoints.length}`
        );
      }

      const [first, ...rest] = shapePoints as [Coordinates, ...Coordinates[]];
      const last = rest[rest.length - 1] ?? first;
      const ring: [number, number][] = [
        [first.lng, first.lat],
        ...rest.map((point): [number, number] => [point.lng, point.lat]),
      ];
      if (last.lng !== first.lng || last.lat !== first.lat) {
        ring.push([first.lng, first.lat]);
      }

      polygon = turf.polygon([ring]);
      break;
    }

    default:
      throw new Error(
        `Unknown shape type: ${shapeType} for geometry ${geometry.id}`
      );
  }

  // Add geometry metadata as properties
  return {
    ...polygon,
    properties: {
      ...polygon.properties,
      geometryId: geometry.id,
      shapeType,
    },
  };
}
